import * as THREE from 'three';
// import Stats from 'stats-js';
// import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader';
import { Events } from './Events/Events';
import { PhysicsWorld } from './Physics/Physics';
import { store } from '../../store/store';
import { DeviceOrientationControls } from 'three/examples/jsm/controls/DeviceOrientationControls.js';
import { PointerLockControls } from 'three/examples/jsm/controls/PointerLockControls';
import { LoadingManager } from './LoadingManager/LoadingManager';
import { WebXR } from './WebXR/WebXR';
import { Sky } from 'three/examples/jsm/objects/Sky.js';
import { Water } from './Water/Water';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { physicalObjects, portraitNames } from '../../utils/metaData';
import { CollissionMesh } from './CollisionMesh/CollissionMesh';
const sizes = {
    width: window.innerWidth,
    height: window.innerHeight
};
export class Scene {
    constructor(el) {
        this.renderer = new THREE.WebGLRenderer({ canvas: el });
        this.renderer.setSize(sizes.width, sizes.height);
        this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
        this.renderer.outputEncoding = THREE.sRGBEncoding;
        this.scene = new THREE.Scene();
        this.sun = new THREE.Vector3();
        this.sky = new Sky();
        this.sky.scale.setScalar(450000);
        this.scene.add(this.sky);
        this.collissionMesh = new CollissionMesh(this.scene);
        const waterGeometry = new THREE.PlaneGeometry(20000, 20000);
        this.water = new Water(waterGeometry, {
            textureWidth: 512,
            textureHeight: 512,
            waterNormals: new THREE.TextureLoader().load('super-gallery/static/waternormals.jpg', function (texture) {
                texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
            }),
            sunDirection: new THREE.Vector3(),
            sunColor: 0xffffff,
            waterColor: 0x17727a,
            distortionScale: 3.7
        });
        this.water.rotation.x = -Math.PI / 2;
        this.water.position.y = -1;
        // this.scene.add(this.water);
        const uniforms = this.sky.material.uniforms;
        const phi = THREE.MathUtils.degToRad(90 - 50);
        const theta = THREE.MathUtils.degToRad(180);
        this.sun.setFromSphericalCoords(1, phi, theta);
        uniforms['turbidity'].value = 10;
        uniforms['rayleigh'].value = 1.246;
        uniforms['mieCoefficient'].value = 0.069;
        uniforms['mieDirectionalG'].value = 0.7;
        uniforms['sunPosition'].value.copy(this.sun);
        this.camera = new THREE.PerspectiveCamera(75, sizes.width / sizes.height, 0.1, 100);
        this.camera.position.x = 3;
        this.camera.rotateY(135);
        const light = new THREE.AmbientLight(0x404040); // soft white light
        this.scene.add(light);
        this.raycaster = new THREE.Raycaster();
        this.pointer = new THREE.Vector3();
        store.isMobileDevice.subscribe(value => {
            this.isMobile = value;
        });
        this.loadingManager = new LoadingManager(this.scene);
        // const axesHelper = new AxesHelper(5);
        // this.scene.add(axesHelper);
        this.textureLoader = new THREE.TextureLoader(this.loadingManager.loadingManager);
        this.attributionTexture = this.textureLoader.load('./super-gallery/static/models/Attribution.jpg');
        this.innerDoorTexture = this.textureLoader.load('./super-gallery/static/models/Pintu.jpg');
        this.areaMapTexture = this.textureLoader.load('./super-gallery/static/models/Denah.jpg');
        this.floorTexture = this.textureLoader.load('./super-gallery/static/models/Lantai_Baru.jpg');
        this.jerseyTexture = this.textureLoader.load('./super-gallery/static/models/Jersey.jpg');
        this.marbleTexture = this.textureLoader.load('./super-gallery/static/models/Marble.jpg');
        this.signUpstairsTexture = this.textureLoader.load('./super-gallery/static/models/Sign_Upstairs.jpg');
        this.signUpstairs002Texture = this.textureLoader.load('./super-gallery/static/models/Sign_Upstairs002.jpg');
        this.toolsTexture = this.textureLoader.load('./super-gallery/static/models/Peralatan.jpg');
        this.shoes01Texture = this.textureLoader.load('./super-gallery/static/models/Sepatu_01.jpg');
        this.shoes02Texture = this.textureLoader.load('./super-gallery/static/models/Sepatu_02.jpg');
        this.warehouse003Texture = this.textureLoader.load('./super-gallery/static/models/Warehouse003.jpg');
        this.warehouse007Texture = this.textureLoader.load('./super-gallery/static/models/Warehouse007.jpg');
        this.toolboxTexture = this.textureLoader.load('./super-gallery/static/models/Toolbox.jpg');
        this.kursiTexture = this.textureLoader.load('./super-gallery/static/models/Kursi.jpg');
        this.kursiMarmerTexture = this.textureLoader.load('./super-gallery/static/models/Kursi_Marmer.jpg');
        this.penyanggaTexture = this.textureLoader.load('./super-gallery/static/models/Penyangga.jpg');
        this.sketchupTexture = this.textureLoader.load('./super-gallery/static/models/Sketchup.jpg');
        this.sketchup001Texture = this.textureLoader.load('./super-gallery/static/models/Sketchup001.jpg');
        this.T_Shirt001Texture = this.textureLoader.load('./super-gallery/static/models/Jersey_Away.jpg');
        this.T_Shirt002Texture = this.textureLoader.load('./super-gallery/static/models/Jersey_Home.jpg');
        this.podiumTexture = this.textureLoader.load('./super-gallery/static/models/Podium.jpg');
        this.tacticalBoardTexture = this.textureLoader.load('./super-gallery/static/models/Tactical_Board.jpg');
        this.loadTextures([
            this.attributionTexture,
            this.innerDoorTexture,
            this.areaMapTexture,
            this.floorTexture,
            this.jerseyTexture,
            this.marbleTexture,
            this.signUpstairsTexture,
            this.signUpstairs002Texture,
            this.toolsTexture,
            this.shoes01Texture,
            this.shoes02Texture,
            this.warehouse003Texture,
            this.warehouse007Texture,
            this.toolboxTexture,
            this.kursiTexture,
            this.kursiMarmerTexture,
            this.penyanggaTexture,
            this.sketchupTexture,
            this.sketchup001Texture,
            this.T_Shirt001Texture,
            this.T_Shirt002Texture,
            this.podiumTexture,
            this.tacticalBoardTexture
        ]);
        if (true) {
            this.events = new Events(this.camera, this.isMobile);
        }
        this.clock = new THREE.Clock();
        this.physics = new PhysicsWorld({
            scene: this.scene,
        });
        if (this.mesh) {
            this.scene.add(this.mesh);
        }
        if (true) {
            this.deviceOrientationControls = new DeviceOrientationControls(this.camera);
            store.deviceOrientation.update(() => this.deviceOrientationControls);
            this.deviceOrientationControls.enabled = false;
        }
        this.dracoLoader = new DRACOLoader(this.loadingManager.loadingManager);
        this.dracoLoader.setDecoderPath('super-gallery/draco/');
        this.gltfLoader = new GLTFLoader(this.loadingManager.loadingManager);
        this.gltfLoader.setDRACOLoader(this.dracoLoader);
        this.gltfLoader.load('./super-gallery/static/Djarum_v1.glb', gltf => this.handleGltf(gltf));
        store.isMobileDevice.subscribe(v => (this.isMobile = v));
        this.webXR = new WebXR({
            renderer: this.renderer,
            camera: this.camera,
            scene: this.scene
        });
        this.renderPass = new RenderPass(this.scene, this.camera);
        this.composer = new EffectComposer(this.renderer);
        this.composer.addPass(this.renderPass);
        if (!this.isMobile) {
            store.scrollSpeed.subscribe(v => {
                if (!this.currentSession) {
                    this.camera.rotation.y = this.camera.rotation.y + v * 0.00099;
                }
            });
            store.pointerLockerControls.update(() => new PointerLockControls(this.camera, el));
            this.events.handleKeyUpEvents();
            this.events.handleKeyDownEvents();
        }
        this.resize();
        this.pointerMove();
        this.render(this.isMobile);
    }
    resize() {
        window.addEventListener('resize', () => {
            sizes.width = window.innerWidth;
            sizes.height = window.innerHeight;
            this.camera.aspect = sizes.width / sizes.height;
            this.camera.updateProjectionMatrix();
            this.renderer.setSize(sizes.width, sizes.height);
            this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
            // this.labelRenderer.setSize(sizes.width, sizes.height);
        });
    }
    pointerMove() {
        window.addEventListener('pointermove', event => {
            this.pointer.x = (event.clientX / window.innerWidth) * 2 - 1;
            this.pointer.y = -(event.clientY / window.innerHeight) * 2 + 1;
            // update the picking ray with the camera and pointer position
            this.raycaster = new THREE.Raycaster();
            this.raycaster.setFromCamera(this.pointer, this.camera);
            // See if the ray from the camera into the world hits one of our meshes
            const intersects = this.raycaster.intersectObjects(this.scene.children, true);
            intersects.forEach(function (intersect) {
                if (intersect.object.name == 'Peralatan') {
                    // let proj = intersect.object.position.clone().project(camera);
                    // alert('Peralatan');
                }
            });
        });
    }
    handleGltf(gltf) {
        gltf.scene.traverse(child => {
            console.log(child.name);
            if (physicalObjects.includes(child.name)) {
                this.physics.createPhysics(child.name, child);
            }
            if (child.name === 'Floor004') {
                child.material = new THREE.MeshBasicMaterial({ map: this.floorTexture });
            }
            if (child.name === 'Cube006') {
                child.material = new THREE.MeshBasicMaterial({ map: this.innerDoorTexture });
            }
            if (child.name === 'Cube007') {
                child.material = new THREE.MeshBasicMaterial({ map: this.attributionTexture });
            }
            if (child.name === 'Cube008') {
                child.material = new THREE.MeshBasicMaterial({ map: this.areaMapTexture });
            }
            if (child.name === 'Jersey') {
                child.material = new THREE.MeshBasicMaterial({ map: this.jerseyTexture });
            }
            if (child.name === 'Sepatu_01') {
                child.material = new THREE.MeshBasicMaterial({ map: this.shoes01Texture });
            }
            if (child.name === 'Sepatu_02') {
                child.material = new THREE.MeshBasicMaterial({ map: this.shoes02Texture });
            }
            if (child.name === 'Warehouse001') {
                child.material = new THREE.MeshBasicMaterial({ map: this.warehouse003Texture });
            }
            if (child.name === 'Warehouse003') {
                child.material = new THREE.MeshBasicMaterial({ map: this.warehouse003Texture });
            }
            if (child.name === 'Warehouse007') {
                child.material = new THREE.MeshBasicMaterial({ map: this.warehouse007Texture });
            }
            if (child.name === 'modular-box-group-04002_electrical_boxes_0') {
                child.material = new THREE.MeshBasicMaterial({ map: this.toolboxTexture });
            }
            if (child.name === 'modular-box-group-04002_electrical_boxes_0001') {
                child.material = new THREE.MeshBasicMaterial({ map: this.toolboxTexture });
            }
            if (child.name === 'modular-box-group-04002_electrical_boxes_0002') {
                child.material = new THREE.MeshBasicMaterial({ map: this.toolboxTexture });
            }
            if (child.name === 'modular-box-group-04002_electrical_boxes_0003') {
                child.material = new THREE.MeshBasicMaterial({ map: this.toolboxTexture });
            }
            if (child.name === 'Kursi_kayu') {
                child.material = new THREE.MeshBasicMaterial({ map: this.kursiTexture });
            }
            if (child.name === 'Kursi_kayu001') {
                child.material = new THREE.MeshBasicMaterial({ map: this.kursiTexture });
            }
            if (child.name === 'Circle003') {
                child.material = new THREE.MeshBasicMaterial({ map: this.kursiMarmerTexture });
            }
            if (child.name === 'Circle001') {
                child.material = new THREE.MeshBasicMaterial({ map: this.kursiMarmerTexture });
            }
            if (child.name === 'Cube011') {
                child.material = new THREE.MeshBasicMaterial({ map: this.penyanggaTexture });
            }
            if (child.name === 'Peralatan') {
                child.material = new THREE.MeshBasicMaterial({ map: this.toolsTexture });
            }
            if (child.name === 'Marble') {
                child.material = new THREE.MeshBasicMaterial({ map: this.marbleTexture });
            }
            if (child.name === 'Sign_Upstairs' || child.name === 'Sign_Upstairs001') {
                child.material = new THREE.MeshBasicMaterial({ map: this.signUpstairsTexture });
            }
            if (child.name === 'Sign_Upstairs002') {
                child.material = new THREE.MeshBasicMaterial({ map: this.signUpstairs002Texture });
            }
            if (child.name === 'Sketchup') {
                child.material = new THREE.MeshBasicMaterial({ map: this.sketchupTexture });
            }
            if (child.name === 'Sketchup001') {
                child.material = new THREE.MeshBasicMaterial({ map: this.sketchup001Texture });
            }
            if (child.name === 'T_Shirt001') {
                child.material = new THREE.MeshBasicMaterial({ map: this.T_Shirt001Texture });
            }
            if (child.name === 'T_Shirt002') {
                child.material = new THREE.MeshBasicMaterial({ map: this.T_Shirt002Texture });
            }
            if (child.name === 'Podium') {
                child.material = new THREE.MeshBasicMaterial({ map: this.podiumTexture });
            }
            if (child.name === 'Cube005') {
                child.material = new THREE.MeshBasicMaterial({ map: this.tacticalBoardTexture });
            }
            this.addPortraits(gltf);
            this.scene.add(gltf.scene);
        });
    }
    addPortraits(gltfScene) {
        portraitNames.forEach(creative => {
            if (creative.slug === 'HiddenObject' || creative.slug === 'ArtworkCreator')
                return;
            const portrait = this.textureLoader.load(`./super-gallery/static/artwork/${creative.slug}.jpg`);
            portrait.flipY = false;
            portrait.minFilter = THREE.LinearFilter;
            const material = new THREE.MeshBasicMaterial({ map: portrait });
            const label = this.textureLoader.load(`./super-gallery/static/artwork/${creative.slug}-label.jpg`);
            label.flipY = false;
            label.minFilter = THREE.LinearFilter;
            const labelMaterial = new THREE.MeshBasicMaterial({ map: label });
            const mesh = gltfScene.scene.children.find(child => child.name === creative.slug);
            const meshLabel = gltfScene.scene.children.find(child => child.name === creative.label);
            mesh.material = material;
            meshLabel.material = labelMaterial;
        });
    }
    loadTextures(textures) {
        textures.forEach(texture => {
            texture.flipY = false;
            texture.encoding = THREE.sRGBEncoding;
        });
    }
    render(isMobileDevice) {
        // console.log(this.events.fwdValue);
        // stats.begin();
        const isMobile = isMobileDevice;
        const elapsedTime = this.clock.getElapsedTime();
        if (this.water) {
            this.water.material.uniforms['time'].value += 1.0 / 60.0;
        }
        // this.controls.update();
        if (this.physics) {
            this.physics.handlePhysics({ elapsedTime, camera: this.camera, userDirection: this.events.userDirection });
        }
        if (this.isMobile) {
            this.deviceOrientationControls.update();
        }
        this.composer.render();
        // stats.end();
        window.requestAnimationFrame(() => this.render(isMobile));
    }
}
